import { Box, FormControl, InputLabel, MenuItem, Select, TextField, Typography } from "@mui/material";
import { useEffect, useState } from "react";

export interface IDimensoesEtiqueta {
    largura: number;
    altura: number;
}

/** Mesmos limites do ms-pedidos (LIMITES_DIMENSAO_ETIQUETA). */
export const LIMITES_DIMENSAO_ETIQUETA = { minimo: 20, maximo: 150 };

/** Rolos mais comuns nas impressoras térmicas de etiqueta (largura x altura, em mm). */
const TAMANHOS_COMUNS: IDimensoesEtiqueta[] = [
    { largura: 40, altura: 25 },
    { largura: 50, altura: 25 },
    { largura: 50, altura: 30 },
    { largura: 60, altura: 40 },
    { largura: 80, altura: 40 },
    { largura: 100, altura: 50 },
    { largura: 100, altura: 100 },
];

const PERSONALIZADO = "personalizado";

export const dimensaoEtiquetaValida = (valor?: number) =>
    Number.isInteger(valor) &&
    (valor as number) >= LIMITES_DIMENSAO_ETIQUETA.minimo &&
    (valor as number) <= LIMITES_DIMENSAO_ETIQUETA.maximo;

interface TamanhoEtiquetaInputProps {
    valor: IDimensoesEtiqueta;
    onChange: (valor: IDimensoesEtiqueta) => void;
}

/**
 * Tamanho do rolo da etiqueta de produto.
 *
 * Os campos guardam o texto digitado: antes o valor vinha de `dimensao || 60`, então
 * apagar o campo mostrava 60 de novo e o próximo dígito virava "601" — o tamanho
 * nunca era salvo como a pessoa digitou.
 */
export const TamanhoEtiquetaInput = ({ valor, onChange }: TamanhoEtiquetaInputProps) => {
    const [largura, setLargura] = useState(String(valor.largura));
    const [altura, setAltura] = useState(String(valor.altura));

    useEffect(() => {
        setLargura(String(valor.largura));
        setAltura(String(valor.altura));
    }, [valor.largura, valor.altura]);

    const alterar = (campo: keyof IDimensoesEtiqueta, texto: string) => {
        const somenteDigitos = texto.replace(/\D/g, "").slice(0, 3);
        campo === "largura" ? setLargura(somenteDigitos) : setAltura(somenteDigitos);

        const numero = Number(somenteDigitos);
        if (somenteDigitos && dimensaoEtiquetaValida(numero)) {
            onChange({ ...valor, [campo]: numero });
        }
    };

    const erro = (texto: string) => !dimensaoEtiquetaValida(Number(texto));
    const { minimo, maximo } = LIMITES_DIMENSAO_ETIQUETA;

    const tamanhoSelecionado = TAMANHOS_COMUNS.find(
        tamanho => tamanho.largura === valor.largura && tamanho.altura === valor.altura
    );

    return (
        <Box>
            <FormControl fullWidth size="small" sx={{ marginTop: "0.5rem" }}>
                <InputLabel>Tamanho do rolo</InputLabel>
                <Select
                    label="Tamanho do rolo"
                    value={tamanhoSelecionado ? `${tamanhoSelecionado.largura}x${tamanhoSelecionado.altura}` : PERSONALIZADO}
                    onChange={evento => {
                        const tamanho = TAMANHOS_COMUNS.find(
                            item => `${item.largura}x${item.altura}` === evento.target.value
                        );
                        if (tamanho) onChange(tamanho);
                    }}
                >
                    {TAMANHOS_COMUNS.map(tamanho => (
                        <MenuItem key={`${tamanho.largura}x${tamanho.altura}`} value={`${tamanho.largura}x${tamanho.altura}`}>
                            {tamanho.largura} x {tamanho.altura} mm
                        </MenuItem>
                    ))}
                    <MenuItem value={PERSONALIZADO}>Personalizado</MenuItem>
                </Select>
            </FormControl>
            <Box sx={{ display: "flex", gap: "1rem", marginTop: "1rem" }}>
                <TextField
                    label="Largura (mm)"
                    size="small"
                    fullWidth
                    InputLabelProps={{ shrink: true }}
                    inputProps={{ inputMode: "numeric" }}
                    value={largura}
                    onChange={evento => alterar("largura", evento.target.value)}
                    onBlur={() => setLargura(String(valor.largura))}
                    error={erro(largura)}
                />
                <TextField
                    label="Altura (mm)"
                    size="small"
                    fullWidth
                    InputLabelProps={{ shrink: true }}
                    inputProps={{ inputMode: "numeric" }}
                    value={altura}
                    onChange={evento => alterar("altura", evento.target.value)}
                    onBlur={() => setAltura(String(valor.altura))}
                    error={erro(altura)}
                />
            </Box>
            <Typography
                variant="caption"
                color={erro(largura) || erro(altura) ? "error" : "text.secondary"}
                display="block"
                sx={{ marginTop: "0.25rem" }}
            >
                {erro(largura) || erro(altura)
                    ? `Use valores inteiros entre ${minimo} e ${maximo} mm. Ao sair do campo, volta o último tamanho válido.`
                    : `Entre ${minimo} e ${maximo} mm. Use o tamanho exato do rolo da impressora.`}
            </Typography>
        </Box>
    );
};
